import React, { useEffect, useState } from 'react'
import { SurveyQuestionType } from '../models/Survey'
import type { Division } from '../models/Division'
import type { SurveyItem } from '../models/Survey'
import { Typography, Stack, Box, Chip, useTheme } from '@mui/material'

import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { green, grey, shade } from '../colors'
import { type ISurveyItem } from '../models/SurveyTemplate'

// @TODO All helpers are in same file. we should have separate different files for each other

// return percent from 0 to 100
export function getPercent (part: number, total: number): number {
  if (total === 0) return 0
  const result = (100 * part) / total
  if (result < 1 && result > 0) {
    return 1
  }
  return Math.round(result)
}

// return average score, with each item in the list weighted (index 0 is 1, index 1 is 2, etc).
// Total is the total number of entries
export function getAverageScore (values: number[]): number {
  const total = values?.reduce((acc, val) => acc + val, 0)
  if (total === 0) return 0
  return values?.reduce((acc, cur, i) => acc + (i + 1) * cur, 0) / total
}

export function roundedPercentages (percentages: number[]): number[] {
  const totalPercentage = percentages?.reduce((sum, percentage) => sum + percentage, 0)
  const roundedPercentages = percentages?.map(percentage => Math.floor((percentage / totalPercentage) * 100))

  const remainder = 100 - roundedPercentages?.reduce((sum, percentage) => sum + percentage, 0)
  const sortedIndexes = Array.from({ length: percentages.length }, (_, index) => index).sort((a, b) => percentages[b] - percentages[a])

  for (let i = 0; i < remainder; i++) {
    roundedPercentages[sortedIndexes[i % percentages.length]]++
  }

  return roundedPercentages
}

export function breadcrumbToList (division: Division): Division[] {
  let cur: Division | null = division
  const divisions: Division[] = []

  while (cur != null) {
    divisions.push(cur)
    cur = cur?.children != null && cur.children.length > 0 ? cur?.children[0] : null
  }
  return divisions
}

export function getDivisionFromBreadcrumb (breadcrumb: Division | null): Division | null {
  if (breadcrumb === null) {
    return null
  }

  const divisions = breadcrumbToList(breadcrumb)

  if (divisions?.length === 0) {
    return null
  }

  return divisions[divisions?.length - 1]
}

const countNonDescriptiveQuestions = (surveyItems: ISurveyItem[]): number => {
  return surveyItems?.filter((question: ISurveyItem) => question.itemType !== SurveyQuestionType.Title)?.length
}

export function handleSurveyItems<T extends SurveyItem> (
  surveySections: any[],
  handleSurveyItem: (item: T, surveyItem?: any) => JSX.Element
): JSX.Element {
  const theme = useTheme()

  if (!Array.isArray(surveySections)) {
    console.error('Expected surveySections to be an array but got:', surveySections)
    return <></>
  }

  const renderItems = (items: T[]): JSX.Element => {
    let titles: T[] = []
    let questionNumber = 0

    return (
      <Stack role="region">
        {items?.map((child, i, arr) => {
          if (child.itemType !== SurveyQuestionType.Title) {
            questionNumber += 1
          }

          if (child?.itemType === SurveyQuestionType.Title) {
            titles?.push(child)
            if (i === arr?.length - 1) {
              const toRender = titles.map((titleItem, idx) =>
                handleSurveyItem(titleItem, items[idx])
              )
              titles = []
              return <div key={`titles-final-${i}`}>{toRender}</div>
            }
            return null
          }

          if (titles?.length > 0) {
            const titleElems = (
              <div key={`titles-block-${i}`}>
                {titles.map((titleItem, idx) =>
                  handleSurveyItem(titleItem, items[idx])
                )}
              </div>
            )
            titles = []
            return titleElems
          }

          return handleSurveyItem(child, child)
        })}
      </Stack>
    )
  }

  return (
    <>
      {surveySections.map((section, idx) => (
        <Accordion
          elevation={0}
          key={idx}
          sx={{
            border: `1px solid ${shade[650]}`,
            p: `${theme.spacing(4)} ${theme.spacing(6)}`,
            borderRadius: theme.spacing(2),
            '&::before': { content: 'none' }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandLessIcon sx={{ color: grey[250] }} />}
            aria-controls={`panel${idx}-content`}
            id={`panel${idx}-header`}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                pr: 3
              }}
            >
              <Box>
                <Typography fontSize={20} fontWeight={600} color={grey[250]}>
                {section?.title?.trim() ? section.title : section.name}
                </Typography>
                {section.isRepeatPerInstructor && (
                  <Typography fontSize={16} color={grey[850]}>
                    {section.instructorName}
                  </Typography>
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={3}>
                {section.isRepeatPerInstructor && (
                  <Chip
                    variant="outlined"
                    color="success"
                    label="Repeat per Instructor"
                    sx={{
                      borderRadius: 6,
                      border: `1px solid ${green[50]}`,
                      fontWeight: 500,
                      color: green[100],
                      px: 1,
                      backgroundColor: green[0]
                    }}
                  />
                )}
                <Chip
                  variant="outlined"
                  label={`${countNonDescriptiveQuestions(section.surveyItems)} Questions`}
                  sx={{
                    borderRadius: 6,
                    border: `1px solid ${shade[650]}`,
                    fontWeight: 500,
                    color: grey[250]
                  }}
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {renderItems(section.surveyItems || [])}
          </AccordionDetails>
        </Accordion>
      ))}
    </>
  )
}

export function titleFormatted (title: string = ''): JSX.Element {
  if (title === '') {
    return (<></>)
  }

  const colonPos = title.indexOf(':')

  if (colonPos > 0) {
    const bolded = title.substring(0, colonPos + 1)
    const after = title.substring(colonPos + 1)
    return (<Typography tabIndex={0} component="h2" variant="h6"><b>{bolded}</b>{after}</Typography>)
  }

  return (<Typography tabIndex={0} component="h2" variant="h5">{title}</Typography>)
}
